import type { LocaleCode } from '@/i18n/locales'
import { extractAffirmation } from '@/voice/nlu/extract'
import { normalize, words } from '@/voice/nlu/normalize'

/**
 * The ways somebody says "not this one".
 *
 * `extractAffirmation` already reads "skip", "pass", "rather not" and their
 * Hausa equivalents, and it is asked first. What it does not carry is the
 * deferral: "later", "not now", "another time". Those belong here rather than
 * in the shared extractor because they mean something specific to onboarding.
 * In the open conversation "later" is usually part of an answer, and adding it
 * to the general skip list would make "I will go to the market later" read as
 * a refusal.
 *
 * The distinction this file exists to keep is that none of these is a refusal
 * of the whole conversation. They decline one question. A flow that treats
 * "later" at the consent question as a permanent no has recorded a decision
 * the person did not make, and has recorded it in the one place where getting
 * it wrong costs the most.
 */

const DEFERRALS: Record<LocaleCode, readonly string[]> = {
  en: ['later', 'not now', 'another time', 'some other time', 'next time', 'not yet', 'in a bit'],
  ha: ['daga baya', 'ba yanzu', 'sai wani lokaci', 'wani lokaci', 'ba tukuna', 'sai an jima'],
}

/**
 * True when the answer declines this question without answering it.
 *
 * Deliberately checked before the step's own parser at every step. A name
 * parser handed "later" returns "Later" at high enough confidence to be kept,
 * and then greets somebody as Later for a year.
 */
export function isDeferral(transcript: string, locale: LocaleCode): boolean {
  if (extractAffirmation(transcript, locale).value === 'skip') return true
  const text = normalize(transcript)
  if (text.length === 0) return false
  const tokens = words(text)
  return DEFERRALS[locale].some((phrase) => {
    const needle = normalize(phrase)
    /*
     * A single word is matched as a word, not as a substring. "Later" is
     * inside "Slater", which is a surname, and the name step runs this check
     * before its own parser, so a substring match meant somebody called Slater
     * had their name treated as a request to be asked another time.
     */
    return needle.includes(' ') ? text.includes(needle) : tokens.includes(needle)
  })
}
